import { assertNotNil, createTopHoldingsKey, logExceptionIntoSentry, type OperationError, useQuery } from 'venn-utils';
import { getTopHoldings, type OperationResult, type TopHoldingsDataResponse } from 'venn-api';
import { useRecoilValue } from 'recoil';
import {
  blockLimitedHoldingsSubjectsWithFees,
  convertStudioSubjectToHoldingsSubject,
  proxySetIdState,
} from 'venn-state';

type TopHoldingsResult =
  | {
      state: 'READY';
      data: TopHoldingsDataResponse;
    }
  | {
      state: 'ERROR';
      error: string | undefined;
    };

export const useTopHoldings = (blockId: string): TopHoldingsResult => {
  const allSubjects = useRecoilValue(blockLimitedHoldingsSubjectsWithFees(blockId));
  const holdingsSubjects = allSubjects.map(convertStudioSubjectToHoldingsSubject);
  const proxySetId = useRecoilValue(proxySetIdState);

  const { data, error } = useQuery<TopHoldingsDataResponse, OperationResult<OperationError | undefined>>(
    createTopHoldingsKey(holdingsSubjects, proxySetId),
    async () => {
      const { content } = await getTopHoldings(proxySetId, holdingsSubjects);
      return content;
    },
    {
      onError: (error) => {
        logExceptionIntoSentry(error);
      },
      suspense: true,
    },
  );

  return error
    ? {
        state: 'ERROR',
        error: error.content?.body,
      }
    : {
        state: 'READY',
        // Data should not be nil, due to suspense
        data: assertNotNil(data),
      };
};
